import React, { useState, useMemo } from "react";
import { Search, ArrowUpDown, X, Download, Printer } from "lucide-react";
import { toCSV } from "../lib/api";

export function PageHeader({ title, subtitle, children }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-slate-900 font-head">{title}</h1>
        {subtitle && <p className="text-sm text-slate-500 mt-0.5">{subtitle}</p>}
      </div>
      <div className="flex items-center gap-2">{children}</div>
    </div>
  );
}

const btnBase =
  "inline-flex items-center gap-2 rounded-md text-sm font-medium transition-colors focus:outline-none disabled:opacity-50 disabled:pointer-events-none";
export function Button({ variant = "primary", size = "md", className = "", children, ...p }) {
  const variants = {
    primary: "bg-blue-800 text-white hover:bg-blue-900 shadow-sm",
    secondary: "bg-white text-slate-700 border border-slate-300 hover:bg-slate-50",
    danger: "bg-red-600 text-white hover:bg-red-700",
    ghost: "text-slate-600 hover:bg-slate-100",
    success: "bg-emerald-600 text-white hover:bg-emerald-700",
  };
  const sizes = { sm: "px-2.5 py-1.5 text-xs", md: "px-3.5 py-2", lg: "px-5 py-2.5" };
  return (
    <button className={`${btnBase} ${variants[variant]} ${sizes[size]} ${className}`} {...p}>
      {children}
    </button>
  );
}

export function StatusBadge({ status }) {
  const s = (status || "").toLowerCase();
  let cls = "bg-slate-100 text-slate-700 border-slate-200";
  if (["fully received", "received", "completed", "finished", "dispatched", "active"].some((x) => s.includes(x)))
    cls = "bg-emerald-50 text-emerald-700 border-emerald-200";
  else if (["partially", "in transit", "in production", "issued", "ready", "with jobber"].some((x) => s.includes(x)))
    cls = "bg-sky-50 text-sky-700 border-sky-200";
  else if (["ordered", "pending"].some((x) => s.includes(x)))
    cls = "bg-amber-50 text-amber-700 border-amber-200";
  else if (["short", "rejected", "cancelled", "overdue"].some((x) => s.includes(x)))
    cls = "bg-red-50 text-red-700 border-red-200";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${cls}`}>
      {status || "-"}
    </span>
  );
}

export function Card({ className = "", children }) {
  return (
    <div className={`bg-white border border-slate-200 rounded-lg shadow-sm ${className}`}>{children}</div>
  );
}

export function Field({ label, children, required, className = "" }) {
  return (
    <div className={className}>
      <label className="block text-xs font-medium text-slate-600 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {children}
    </div>
  );
}

const inputCls =
  "w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-800 focus:border-blue-600 focus:ring-1 focus:ring-blue-600 outline-none bg-white";
const fieldCls = (extra = "") => `${/(^|\s)w-/.test(extra) ? inputCls.replace("w-full ", "") : inputCls} ${extra}`;
export const Input = React.forwardRef(({ className, ...props }, ref) => (
  <input ref={ref} {...props} className={fieldCls(className)} />
));
export const Textarea = ({ className, rows, ...props }) => (
  <textarea rows={rows || 2} {...props} className={fieldCls(className)} />
);
export function Select({ options = [], className = "", placeholder, ...p }) {
  return (
    <select className={`${inputCls} ${className}`} {...p}>
      {placeholder && <option value="">{placeholder}</option>}
      {options.map((o) => (
        <option key={o.value ?? o} value={o.value ?? o}>
          {o.label ?? o}
        </option>
      ))}
    </select>
  );
}

export function Modal({ open, onClose, title, children, wide, xwide }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/50 p-4 no-print">
      <div
        className={`bg-white rounded-xl shadow-2xl my-8 w-full ${xwide ? "max-w-6xl" : wide ? "max-w-4xl" : "max-w-2xl"} animate-fadein`}
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3.5 sticky top-0 bg-white rounded-t-xl">
          <h3 className="text-lg font-semibold text-slate-800 font-head">{title}</h3>
          <button onClick={onClose} data-testid="modal-close-btn" className="text-slate-400 hover:text-slate-700">
            <X size={20} />
          </button>
        </div>
        <div className="px-5 py-4">{children}</div>
      </div>
    </div>
  );
}

export function KpiCard({ label, value, sub, accent = "blue", icon: Icon, testid, onClick }) {
  const accents = {
    blue: "text-blue-700 bg-blue-50",
    emerald: "text-emerald-700 bg-emerald-50",
    amber: "text-amber-700 bg-amber-50",
    sky: "text-sky-700 bg-sky-50",
    red: "text-red-700 bg-red-50",
    violet: "text-violet-700 bg-violet-50",
  };
  return (
    <div
      data-testid={testid}
      onClick={onClick}
      className={`bg-white border border-slate-200 rounded-lg p-4 shadow-sm hover:shadow transition-all relative overflow-hidden ${onClick ? "cursor-pointer hover:border-blue-300 hover:-translate-y-0.5" : ""}`}
    >
      <div className="flex items-start justify-between">
        <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">{label}</p>
        {Icon && (
          <span className={`p-1.5 rounded-md ${accents[accent]}`}>
            <Icon size={15} />
          </span>
        )}
      </div>
      <p className="text-2xl font-extrabold text-slate-900 mt-2 tabular font-head">{value}</p>
      {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
    </div>
  );
}

export function Tabs({ tabs, active, onChange, testid }) {
  return (
    <div className="flex flex-wrap gap-1 border-b border-slate-200 mb-4" data-testid={testid}>
      {tabs.map((t) => {
        const val = t.value ?? t;
        const label = t.label ?? t;
        return (
          <button
            key={val}
            data-testid={`tab-${String(val).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            onClick={() => onChange(val)}
            className={`px-3.5 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              active === val
                ? "border-blue-700 text-blue-800"
                : "border-transparent text-slate-500 hover:text-slate-800"
            }`}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

export function DataTable({ columns, rows, testid, exportName, onRowClick, dense, actions }) {
  const [q, setQ] = useState("");
  const [sort, setSort] = useState({ key: null, dir: 1 });
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  const filtered = useMemo(() => {
    let r = rows || [];
    if (q) {
      const ql = q.toLowerCase();
      r = r.filter((row) =>
        columns.some((c) => {
          const v = c.accessor ? c.accessor(row) : row[c.key];
          return String(v ?? "").toLowerCase().includes(ql);
        })
      );
    }
    if (sort.key) {
      const col = columns.find((c) => c.key === sort.key);
      r = [...r].sort((a, b) => {
        const va = col.accessor ? col.accessor(a) : a[sort.key];
        const vb = col.accessor ? col.accessor(b) : b[sort.key];
        const na = parseFloat(va), nb = parseFloat(vb);
        if (!isNaN(na) && !isNaN(nb)) return (na - nb) * sort.dir;
        return String(va ?? "").localeCompare(String(vb ?? "")) * sort.dir;
      });
    }
    return r;
  }, [rows, q, sort, columns]);

  const paged = filtered.slice(page * pageSize, page * pageSize + pageSize);
  const totalPages = Math.ceil(filtered.length / pageSize) || 1;

  return (
    <div data-testid={testid}>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="relative w-full max-w-xs">
          <Search size={15} className="absolute left-2.5 top-2.5 text-slate-400" />
          <input
            data-testid={`${testid}-search`}
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(0); }}
            placeholder="Search..."
            className="w-full rounded-md border border-slate-300 pl-8 pr-3 py-2 text-sm outline-none focus:border-blue-600"
          />
        </div>
        <div className="flex items-center gap-2">
          {actions}
          {exportName && (
            <Button variant="secondary" size="sm" onClick={() => toCSV(filtered, exportName)} data-testid={`${testid}-export`}>
              <Download size={14} /> Export
            </Button>
          )}
        </div>
      </div>
      <div className="border border-slate-200 rounded-lg overflow-hidden bg-white shadow-sm overflow-x-auto">
        <table className="w-full text-left">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              {columns.map((c) => (
                <th
                  key={c.key}
                  onClick={() => c.sortable !== false && setSort((s) => ({ key: c.key, dir: s.key === c.key ? -s.dir : 1 }))}
                  className={`px-3 ${dense ? "py-2" : "py-2.5"} text-xs font-semibold text-slate-600 uppercase tracking-wider whitespace-nowrap ${c.sortable !== false ? "cursor-pointer select-none" : ""} ${c.align === "right" ? "text-right" : ""} ${c.cellClass || ""}`}
                >
                  <span className="inline-flex items-center gap-1">
                    {c.header}
                    {c.sortable !== false && <ArrowUpDown size={11} className="text-slate-300" />}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paged.map((row, i) => (
              <tr
                key={row.id || i}
                onClick={() => onRowClick && onRowClick(row)}
                className={`border-b border-slate-100 hover:bg-slate-50/80 transition-colors ${onRowClick ? "cursor-pointer" : ""}`}
              >
                {columns.map((c) => (
                  <td
                    key={c.key}
                    className={`px-3 ${dense ? "py-1.5" : "py-2.5"} text-sm text-slate-700 whitespace-nowrap ${c.align === "right" ? "text-right tabular" : ""} ${c.mono ? "font-mono text-xs" : ""} ${c.cellClass || ""}`}
                  >
                    {c.render ? c.render(row) : c.accessor ? c.accessor(row) : row[c.key] ?? "-"}
                  </td>
                ))}
              </tr>
            ))}
            {!paged.length && (
              <tr>
                <td colSpan={columns.length} className="px-3 py-10 text-center text-sm text-slate-400">
                  No records found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between mt-3 text-xs text-slate-500">
        <div className="flex items-center gap-2">
          <span>{filtered.length} records</span>
          <select
            value={pageSize}
            onChange={(e) => { setPageSize(+e.target.value); setPage(0); }}
            className="border border-slate-300 rounded px-1.5 py-1"
          >
            {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}/page</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>Prev</Button>
          <span>Page {page + 1} / {totalPages}</span>
          <Button variant="secondary" size="sm" disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</Button>
        </div>
      </div>
    </div>
  );
}

export function SizeEditor({ sizes, onChange, testid }) {
  const rows = sizes || [];
  const update = (i, field, val) => {
    const copy = rows.map((r, idx) => (idx === i ? { ...r, [field]: field === "qty" ? val : val } : r));
    onChange(copy);
  };
  const add = () => onChange([...rows, { size: "", qty: 0 }]);
  const remove = (i) => onChange(rows.filter((_, idx) => idx !== i));
  const total = rows.reduce((s, r) => s + (parseFloat(r.qty) || 0), 0);
  return (
    <div data-testid={testid}>
      <div className="space-y-2">
        {rows.map((r, i) => (
          <div key={i} className="flex items-center gap-2">
            <Input placeholder="Size" value={r.size} onChange={(e) => update(i, "size", e.target.value)} className="w-28" data-testid={`${testid}-size-${i}`} />
            <Input type="number" placeholder="Qty" value={r.qty} onChange={(e) => update(i, "qty", e.target.value)} className="w-32" data-testid={`${testid}-qty-${i}`} />
            <button type="button" onClick={() => remove(i)} className="text-red-400 hover:text-red-600"><X size={16} /></button>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between mt-2">
        <Button type="button" variant="secondary" size="sm" onClick={add} data-testid={`${testid}-add`}>+ Add Size</Button>
        <span className="text-sm font-semibold text-slate-700">Total: <span className="tabular">{total}</span></span>
      </div>
    </div>
  );
}

export { Printer, Download };
